import type { ReactNode, ComponentType } from "react";

import type { SizeVariant, WidgetDefinition } from "./types";

interface WidgetFrameProps {
  title: string;
  editing: boolean;
  sizeVariant: SizeVariant;
  availableSizes: SizeVariant[];
  onChangeSize: (variant: SizeVariant) => void;
  onRemove?: () => void;
  Icon?: ComponentType<{ className?: string }>;
  children: ReactNode;
  required?: boolean;
}

const SIZE_LABELS: Record<SizeVariant, string> = {
  xs: "XS",
  s: "S",
  m: "M",
  l: "L",
};

export function WidgetFrame({
  title,
  editing,
  sizeVariant,
  availableSizes,
  onChangeSize,
  onRemove,
  Icon,
  children,
  required,
}: WidgetFrameProps) {
  const showSwitcher = editing && availableSizes.length > 1;
  return (
    <div
      className={`widget-frame widget-frame--${sizeVariant} ${editing ? "widget-frame--editing" : ""}`}
    >
      <header
        className={`widget-frame__header ${editing ? "widget-drag-handle" : ""}`}
      >
        <span className="widget-frame__corner widget-frame__corner--tl" />
        <span className="widget-frame__corner widget-frame__corner--tr" />
        <div className="widget-frame__title-group">
          {Icon && <Icon className="widget-frame__icon" />}
          <span className="widget-frame__title">{title}</span>
        </div>
        <div className="widget-frame__actions">
          {showSwitcher && (
            <div
              className="widget-size-switcher"
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
            >
              {availableSizes.map((size) => (
                <button
                  key={size}
                  type="button"
                  className={`widget-size-switcher__btn ${
                    size === sizeVariant
                      ? "widget-size-switcher__btn--active"
                      : ""
                  }`}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (size !== sizeVariant) onChangeSize(size);
                  }}
                  title={`Resize to ${SIZE_LABELS[size]}`}
                >
                  {SIZE_LABELS[size]}
                </button>
              ))}
            </div>
          )}
          {editing && !required && onRemove && (
            <button
              type="button"
              className="widget-frame__remove"
              onClick={(e) => {
                e.stopPropagation();
                onRemove();
              }}
              onMouseDown={(e) => e.stopPropagation()}
              onTouchStart={(e) => e.stopPropagation()}
              title="Remove widget"
              aria-label={`Remove ${title}`}
            >
              <svg
                viewBox="0 0 24 24"
                width="14"
                height="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          )}
        </div>
      </header>
      <div className="widget-frame__body">{children}</div>
      <span className="widget-frame__corner widget-frame__corner--bl" />
      <span className="widget-frame__corner widget-frame__corner--br" />
    </div>
  );
}

export function availableSizesForWidget(
  def: WidgetDefinition,
): SizeVariant[] {
  const out: SizeVariant[] = [];
  if (def.sizes.xs) out.push("xs");
  if (def.sizes.s) out.push("s");
  if (def.sizes.m) out.push("m");
  if (def.sizes.l) out.push("l");
  return out;
}
